'use client';

import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { Briefcase, BarChart, Users, Menu, X } from 'lucide-react';
import { useState } from 'react';

export function CorporateProfessionalHomepage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <div className="bg-white text-gray-800">
      <header className="bg-gray-50 border-b relative">
        <div className="container mx-auto flex justify-between items-center p-4">
          <h2 className="text-2xl font-bold text-blue-800">Innovate Corp.</h2>
          <nav className="hidden md:flex items-center gap-6">
            <a
              href="#"
              className="text-sm font-medium text-gray-600 hover:text-blue-800"
            >
              Solutions
            </a>
            <a
              href="#"
              className="text-sm font-medium text-gray-600 hover:text-blue-800"
            >
              About Us
            </a>
            <a
              href="#"
              className="text-sm font-medium text-gray-600 hover:text-blue-800"
            >
              Insights
            </a>
            <Button>Request a Demo</Button>
          </nav>
          <div className="md:hidden">
            <Button
              size="icon"
              variant="ghost"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X /> : <Menu />}
            </Button>
          </div>
        </div>
        {isMenuOpen && (
          <div className="md:hidden bg-gray-50 border-t">
            <nav className="container mx-auto flex flex-col items-start gap-4 p-4">
              <a
                href="#"
                className="text-lg font-medium text-gray-600 hover:text-blue-800"
              >
                Solutions
              </a>
              <a
                href="#"
                className="text-lg font-medium text-gray-600 hover:text-blue-800"
              >
                About Us
              </a>
              <a
                href="#"
                className="text-lg font-medium text-gray-600 hover:text-blue-800"
              >
                Insights
              </a>
              <Button className="w-full mt-2">Request a Demo</Button>
            </nav>
          </div>
        )}
      </header>
      <main>
        <section className="bg-gray-50 py-16 md:py-32">
          <div className="container mx-auto grid md:grid-cols-2 gap-12 items-center px-4">
            <div className="text-center md:text-left">
              <h1 className="text-4xl md:text-5xl font-bold text-gray-800 leading-tight">
                Driving Growth Through Strategic Financial Solutions
              </h1>
              <p className="mt-4 text-lg text-gray-600">
                We partner with global enterprises to optimize performance,
                mitigate risk, and achieve sustainable success.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                <Button size="lg">Our Expertise</Button>
                <Button size="lg" variant="outline">
                  Client Stories
                </Button>
              </div>
            </div>
            <div>
              <Image
                src="https://picsum.photos/seed/corp3/1200/800"
                alt="Diverse team of professionals collaborating in a modern office"
                width={1200}
                height={800}
                className="rounded-lg shadow-xl"
                data-ai-hint="team collaboration"
              />
            </div>
          </div>
        </section>

        <section className="py-20 md:py-24 px-4">
          <div className="container mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Core Services</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto mb-12">
              We provide a comprehensive suite of services to address your most
              critical business challenges.
            </p>
            <div className="grid md:grid-cols-3 gap-8 text-left">
              <div className="bg-gray-50 p-6 rounded-lg">
                <Briefcase className="w-8 h-8 text-blue-700 mb-4" />
                <h3 className="font-bold text-lg mb-2">
                  Management Consulting
                </h3>
                <p className="text-gray-600 text-sm">
                  Strategic planning and operational improvements to drive
                  efficiency and growth.
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <BarChart className="w-8 h-8 text-blue-700 mb-4" />
                <h3 className="font-bold text-lg mb-2">Financial Advisory</h3>
                <p className="text-gray-600 text-sm">
                  Expert guidance on mergers, acquisitions, and capital
                  allocation.
                </p>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <Users className="w-8 h-8 text-blue-700 mb-4" />
                <h3 className="font-bold text-lg mb-2">Risk Management</h3>
                <p className="text-gray-600 text-sm">
                  Identifying and mitigating financial, operational, and
                  regulatory risks.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-gray-100 py-20 px-4">
          <div className="container mx-auto">
            <div className="text-center">
              <h2 className="text-3xl font-bold mb-4">Our Impact in Numbers</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto mb-12">
                Data-driven results that speak for themselves.
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <p className="text-4xl md:text-5xl font-bold text-blue-800">150+</p>
                <p className="mt-2 text-gray-600">Global Clients</p>
              </div>
              <div>
                <p className="text-4xl md:text-5xl font-bold text-blue-800">$20B+</p>
                <p className="mt-2 text-gray-600">In Transactions Advised</p>
              </div>
              <div>
                <p className="text-4xl md:text-5xl font-bold text-blue-800">98%</p>
                <p className="mt-2 text-gray-600">Client Retention Rate</p>
              </div>
              <div>
                <div className="flex justify-center items-baseline gap-1">
                  <p className="text-4xl md:text-5xl font-bold text-blue-800">35</p>
                  <span className="text-2xl font-bold text-blue-700">%</span>
                </div>
                <p className="mt-2 text-gray-600">Average Client Growth</p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-blue-800 text-white py-20 px-4">
          <div className="container mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">
              Ready to Transform Your Business?
            </h2>
            <p className="max-w-xl mx-auto mb-8">
              Let's discuss how Innovate Corp. can help you achieve your
              strategic objectives.
            </p>
            <Button
              size="lg"
              variant="secondary"
              className="bg-white text-blue-800 hover:bg-blue-100"
            >
              Get In Touch
            </Button>
          </div>
        </section>
      </main>
      <footer className="bg-gray-800 text-gray-300 py-12 px-4">
        <div className="container mx-auto text-center text-sm">
          <p>
            &copy; {new Date().getFullYear()} Innovate Corp. All rights reserved.
          </p>
          <div className="flex justify-center gap-4 mt-4">
            <a href="#" className="hover:text-white">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-white">
              Terms of Service
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
